import { Directive, ElementRef, HostListener, inject, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';

@Directive({
  selector: 'img[appDefaultImage]',
  standalone: true,
})
/**
 * Directive to set default image if the image is not loaded
 * Can also pass the default image path as an input
 * @example
 * <img [src]="imagePath" appDefaultImage />
 * @example
 * <img [src]="imagePath" appDefaultImage="assets/images/no-image.png" />
 */
export class DefaultImageDirective implements OnInit, OnDestroy {
  // Optional input to override the default image path
  @Input('appDefaultImage') customDefaultImg: string;
  private readonly _el = inject<ElementRef<HTMLImageElement>>(ElementRef);
  private readonly _renderer = inject(Renderer2);
  // Default image path fallback
  private readonly _defaultImg: string = 'assets/images/no-image.png';
  private readonly _loadingClasses = ['background-loading-tiny', 'background-loading-small', 'background-loading'];
  private _observer: MutationObserver;
  private _lastRemovedClass: string;

  /**
   * Initialize the observer to watch for changes in the src attribute
   * and add the background-loading class to the parent element
   */
  ngOnInit() {
    this._observer = new MutationObserver(() => {
      this._addBackgroundLoadingClass();
    });

    this._observer.observe(this._el.nativeElement, {
      attributes: true,
      attributeFilter: ['src'],
    });
  }

  ngOnDestroy() {
    if (this._observer) {
      this._observer.disconnect();
    }
  }

  // If the image fails to load, set the default image
  @HostListener('error') onError() {
    const img: HTMLImageElement = this._el.nativeElement;
    const fallback = this.customDefaultImg || this._defaultImg;

    if (img.src.includes(fallback)) {
      // already tried the fallback once – bail out
      return;
    }

    this._renderer.setAttribute(img, 'src', fallback);
  }

  // If the image loads successfully, remove the background-loading class
  @HostListener('load') onLoad() {
    this._removeBackgroundLoadingClass();
  }

  // Prevent right-click on the image
  @HostListener('contextmenu', ['$event']) onRightClick(event: MouseEvent) {
    event.preventDefault();
  }

  /**
   * Remove the background-loading class from the parent element
   * @private
   */
  private _removeBackgroundLoadingClass() {
    const parentElement = this._el.nativeElement.parentElement;
    if (parentElement) {
      this._loadingClasses.forEach((className) => {
        if (parentElement.classList.contains(className)) {
          this._renderer.removeClass(parentElement, className);
          this._lastRemovedClass = className;
        }
      });
    }
  }

  /**
   * Add the background-loading class to the parent element
   * @private
   */
  private _addBackgroundLoadingClass() {
    const parentElement = this._el.nativeElement.parentElement;
    if (parentElement && this._lastRemovedClass) {
      this._renderer.addClass(parentElement, this._lastRemovedClass);
    }
  }
}
